/**
 * ServiceAreasStep
 *
 * Story 11: Registration Wizard - Service Areas
 *
 * Step 3 of 5 in registration wizard
 *
 * Features:
 * - Display primary county from invite (read-only)
 * - Multi-select for additional counties
 * - Service type checkboxes (waste types)
 * - Form validation
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export interface ServiceAreasData {
  primaryCounty: string;
  additionalCounties?: string[];
  serviceTypes: string[];
}

interface ServiceAreasStepProps {
  onNext: (data: ServiceAreasData) => void;
  onBack: () => void;
  primaryCounty: string;
  initialData?: Partial<ServiceAreasData>;
}

const serviceAreasSchema = z.object({
  primaryCounty: z.string(),
  additionalCounties: z.array(z.string()).optional(),
  serviceTypes: z.array(z.string()).min(1, 'Select at least one service type'),
});

type ServiceAreasFormData = z.infer<typeof serviceAreasSchema>;

const CALIFORNIA_COUNTIES = [
  'Alameda', 'Alpine', 'Amador', 'Butte', 'Calaveras', 'Colusa', 'Contra Costa',
  'Del Norte', 'El Dorado', 'Fresno', 'Glenn', 'Humboldt', 'Imperial', 'Inyo',
  'Kern', 'Kings', 'Lake', 'Lassen', 'Los Angeles', 'Madera', 'Marin', 'Mariposa',
  'Mendocino', 'Merced', 'Modoc', 'Mono', 'Monterey', 'Napa', 'Nevada', 'Orange',
  'Placer', 'Plumas', 'Riverside', 'Sacramento', 'San Benito', 'San Bernardino',
  'San Diego', 'San Francisco', 'San Joaquin', 'San Luis Obispo', 'San Mateo',
  'Santa Barbara', 'Santa Clara', 'Santa Cruz', 'Shasta', 'Sierra', 'Siskiyou',
  'Solano', 'Sonoma', 'Stanislaus', 'Sutter', 'Tehama', 'Trinity', 'Tulare',
  'Tuolumne', 'Ventura', 'Yolo', 'Yuba',
];

const SERVICE_TYPES = [
  { value: 'hauling', label: 'Waste Hauling' },
  { value: 'recycling', label: 'Recycling Services' },
  { value: 'composting', label: 'Composting' },
  { value: 'demolition', label: 'Demolition Debris' },
  { value: 'construction', label: 'Construction Waste' },
  { value: 'special', label: 'Special Waste' },
];

export function ServiceAreasStep({ onNext, onBack, primaryCounty, initialData }: ServiceAreasStepProps) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ServiceAreasFormData>({
    resolver: zodResolver(serviceAreasSchema),
    defaultValues: {
      primaryCounty,
      additionalCounties: initialData?.additionalCounties || [],
      serviceTypes: initialData?.serviceTypes || [],
    },
  });

  const selectedServiceTypes = watch('serviceTypes');

  const onSubmit = (data: ServiceAreasFormData) => {
    onNext(data);
  };

  const availableCounties = CALIFORNIA_COUNTIES.filter(county => county !== primaryCounty);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Service Areas</h2>
        <p className="text-neutral-600">
          Specify the counties you serve and the types of services you provide.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Primary County
          </label>
          <div className="input w-full px-4 py-3 md:py-2 text-base border rounded-lg bg-neutral-50 cursor-not-allowed">
            {primaryCounty}
          </div>
          <p className="mt-1 text-sm text-neutral-500">
            Based on your invite code
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Additional Counties (Optional)
          </label>
          <div className="border border-neutral-300 rounded-md p-4 max-h-60 overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {availableCounties.map((county) => (
                <label key={county} className="flex items-center space-x-2 cursor-pointer min-h-11">
                  <input
                    type="checkbox"
                    value={county}
                    {...register('additionalCounties')}
                    className="checkbox w-5 h-5"
                  />
                  <span className="text-sm text-neutral-700">{county}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Service Types <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3">
            {SERVICE_TYPES.map((serviceType) => (
              <label key={serviceType.value} className="flex items-center space-x-2 cursor-pointer min-h-11">
                <input
                  type="checkbox"
                  value={serviceType.value}
                  {...register('serviceTypes')}
                  className="checkbox w-5 h-5"
                />
                <span className="text-sm text-neutral-700">{serviceType.label}</span>
              </label>
            ))}
          </div>
          {errors.serviceTypes && (
            <p className="mt-2 text-sm text-red-600">{errors.serviceTypes.message}</p>
          )}
          {selectedServiceTypes && selectedServiceTypes.length > 0 && (
            <p className="mt-2 text-sm text-neutral-600">
              {selectedServiceTypes.length} service type{selectedServiceTypes.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between gap-3 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="btn-secondary sm:w-auto order-2 sm:order-1"
          >
            Back
          </button>
          <button
            type="submit"
            className="btn-primary sm:w-auto order-1 sm:order-2"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
}
